<template>
    <div class="c-np col-xl-8 col-lg-8 col-md-8">
        <CCard :extra-style="cardStyle">
            <div :style="eyebrowStyle">You answered "no"</div>
            <h2 :style="titleStyle">
                No worries — what happened in
                <span class="red" :style="accentStyle">{{ currentMonth }}</span>?
            </h2>
            <p :style="leadStyle">
                Pick the reason that fits best. This helps TAG keep the deal in
                the right state for next month.
            </p>

            <div :style="reasonGridStyle">
                <label
                    v-for="(reason, index) in reasons"
                    :key="`${reason.id}__${saleLineItem.period}__${index}`"
                    :for="`reason_${reason.id}`"
                    :style="reasonOptionStyle(selectedReason === reason.id)"
                >
                    <input
                        type="radio"
                        :checked="selectedReason === reason.id"
                        :id="`reason_${reason.id}`"
                        name="reason_id"
                        :value="reason.id"
                        :style="srOnly"
                        @change="$emit('change', $event)"
                    />
                    <span :style="reasonRadioDotStyle(selectedReason === reason.id)">
                        <span v-if="selectedReason === reason.id" :style="reasonRadioInnerStyle" />
                    </span>
                    <span :style="reasonOptionLabelStyle">{{ reason.name }}</span>
                </label>
            </div>

            <div :key="explanationKey" :style="explanationFieldStyle">
                <label :style="labelStyle">Explanation</label>
                <textarea
                    v-model="saleLineItem.explanation"
                    :style="textareaStyle"
                    @input="$emit('update:explanation', $event.target.value)"
                ></textarea>
            </div>
        </CCard>
    </div>
</template>

<script>
import CCard from "../c-design/CCard.vue";
import {
    C_INK,
    C_INK2,
    C_MUTED,
    C_HAIR,
    C_CREAM,
    C_RED,
    FONT_DISPLAY,
    FONT_BODY,
} from "../c-design/tokens";

/**
 * Stage 6 of the wizard redesign. Visual restyle only — preserves the
 * existing prop+emit contract (`change` on radio toggle,
 * `update:explanation` from the textarea, `selectedReason` driving
 * radio :checked state) and the underlying form DOM (radios still
 * carry `name="reason_id"` so the test contract survives unchanged).
 */
export default {
    name: "NoServiceProvidedScreen",
    components: { CCard },
    props: {
        saleLineItem: { type: Object, required: true },
        currentMonth: { type: String, default: "" },
        reasons: { type: Array, default: () => [] },
        selectedReason: { default: null },
    },
    computed: {
        explanationKey() {
            const sli = this.saleLineItem;
            if (!sli.is_new_project && !sli.is_new_retainer) {
                return `${sli.deal_line_item.id}__${sli.period}__explanation`;
            }
            return `${sli.id}__explanation`;
        },
        cardStyle() {
            return { padding: '32px 36px' };
        },
        eyebrowStyle() {
            return {
                fontSize: '12px',
                color: C_MUTED,
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
                fontWeight: 600,
                marginBottom: '6px',
            };
        },
        titleStyle() {
            return {
                margin: 0,
                fontFamily: FONT_DISPLAY,
                fontSize: '28px',
                fontWeight: 700,
                letterSpacing: '-0.7px',
                lineHeight: 1.1,
                color: C_INK,
            };
        },
        accentStyle() {
            return { color: C_RED };
        },
        leadStyle() {
            return {
                margin: '8px 0 22px',
                fontSize: '14px',
                color: C_MUTED,
                lineHeight: 1.55,
            };
        },
        reasonGridStyle() {
            return {
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '10px',
            };
        },
        reasonRadioInnerStyle() {
            return {
                width: '8px', height: '8px', borderRadius: '50%',
                background: '#fff', display: 'inline-block',
            };
        },
        reasonOptionLabelStyle() {
            return { fontSize: '14px', fontWeight: 600, color: 'inherit' };
        },
        srOnly() {
            return {
                position: 'absolute', width: '1px', height: '1px',
                margin: '-1px', overflow: 'hidden',
                clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: '0',
            };
        },
        explanationFieldStyle() {
            return { marginTop: '18px' };
        },
        labelStyle() {
            return {
                display: 'block', fontSize: '12px', color: C_INK2,
                fontWeight: 600, marginBottom: '6px',
            };
        },
        textareaStyle() {
            return {
                width: '100%', minHeight: '90px', padding: '14px',
                border: `1.5px solid ${C_HAIR}`, borderRadius: '14px',
                background: C_CREAM, resize: 'vertical',
                fontFamily: FONT_BODY, fontSize: '14px',
                color: C_INK, outline: 'none',
            };
        },
    },
    methods: {
        reasonOptionStyle(selected) {
            return {
                textAlign: 'left', padding: '14px 16px',
                background: selected ? C_INK : '#fff',
                color: selected ? '#fff' : C_INK,
                border: `1.5px solid ${selected ? C_INK : C_HAIR}`,
                borderRadius: '12px', fontFamily: FONT_BODY,
                cursor: 'pointer', display: 'flex',
                alignItems: 'center', gap: '12px', position: 'relative',
            };
        },
        reasonRadioDotStyle(selected) {
            return {
                width: '18px', height: '18px', borderRadius: '50%',
                border: `1.5px solid ${selected ? '#fff' : C_HAIR}`,
                display: 'inline-flex', alignItems: 'center',
                justifyContent: 'center', flexShrink: 0,
            };
        },
    },
};
</script>

<style scoped>
.c-np .red { color: #E63946; }
</style>
